import { memo } from 'react';
import './buffy.css';
import { clipOf, localAt, posAt, segIndexAt, shadowAt } from '../hero/buffyTrack';
import { cellAt } from '../hero/layout';
import { atlasNow, rowOfClip } from './atlas';
import { CLIPS, frameAt, resolveClip } from './clips';
import { canvasStyle, createPainter } from './painter';
import { addSnapTarget } from './pixelSnap';

/* BuffyActor: the ONE Buffy in the hero (motion spec 3.1). The element is
   rendered once and never unmounted, keyed or swapped. The stage drives it
   every frame through createActor() below.

   Smoothness rules (the 2026-09-29 frame audit):
     - Every sheet lives in ONE pre-decoded atlas (buffy/atlas.js); a clip
       change only reads another row, so no frame is ever blank.
     - The cell shown is computed from the master clock (frameAt): what is on
       screen is exactly the timeline's frame, with no CSS animation start lag
       or drift, and a pause, a seek or a tab switch cannot desync it.
     - The wrapper moves by one transform per frame, on his art-pixel grid, and
       only inside jump arcs.
     - Floor and ledge shadows are CSS ellipses (no filter, no sprite).
   Crispness rules (owner report, same day): he is painted on a <canvas> at a
   whole number of DEVICE px per art pixel (geo.dev; painter.js), and the
   stage origin under him is snapped to the device-pixel grid (pixelSnap.js),
   so his pixels never land between device pixels at any display scaling.

   The hidden .bx-strip mirrors the painted cell (transform = frame, position
   = atlas row) for the audit scripts; it is never painted. */

function BuffyActor({ s, dev, dpr }) {
  const cell = 64 * s;
  return (
    <>
      <i className="bx-shadow" data-surface="HOME" style={{ width: 36 * s, height: 4 * s, marginLeft: -18 * s, marginTop: -2 * s }} />
      <i className="bx-shadow" data-surface="LEDGE" style={{ width: 36 * s, height: 4 * s, marginLeft: -18 * s, marginTop: -2 * s }} />
      <div className="bx-actor" style={{ width: cell, height: cell }}>
        <span className="bx-sprite" style={{ width: cell, height: cell }}>
          <canvas className="bx-canvas" width={64 * dev} height={64 * dev} style={canvasStyle(dev, dpr)} />
          <span className="bx-strip" style={{ height: cell }} />
        </span>
      </div>
    </>
  );
}

export default memo(BuffyActor);

/* The per-frame driver. `tracks` = { first, loop } (the first run opens with
   WAVE, later runs with IDLE). `scene.homeAt(t)` (sceneTimeline) is where his
   feet are at loop time t: beside the composer, which moves with the chat
   group between the middle of the stage and the dock. */
export function createActor(root, geo, tracks, scene = null) {
  const wrap = root.querySelector('.bx-actor');
  const sprite = root.querySelector('.bx-sprite');
  const strip = root.querySelector('.bx-strip');
  const painter = createPainter(root.querySelector('.bx-canvas'));
  const shadows = {
    HOME: root.querySelector('.bx-shadow[data-surface="HOME"]'),
    LEDGE: root.querySelector('.bx-shadow[data-surface="LEDGE"]'),
  };
  const dpr = geo.dpr || 1;
  const cell = painter.scale(geo.dev || geo.s, dpr);
  const pts = { HOME: geo.HOME, LEDGE: geo.LEDGE, lift: geo.lift, arcLift: geo.arcLift, corridor: geo.corridor };
  let atlas = null;
  let clipNow = '';
  let frameNow = -1;
  let rowNow = -1;
  let mirrorNow = null;
  let lastTrack = null;
  let idx = 0;
  let lastPos = '';
  let lastShadow = '';
  let feet = null;
  // the device-grid offset of the stage origin under him (pixelSnap.js)
  let snapX = 0;
  let snapY = 0;

  const adoptAtlasIfReady = () => {
    if (atlas) return;
    const a = atlasNow();
    if (!a) return;
    atlas = a;
    strip.style.backgroundImage = `url("${a.url}")`;
    strip.style.backgroundSize = `${a.cols * cell}px ${a.rows * cell}px`;
    strip.style.width = `${cell}px`; // never wider than his cell (no page overflow)
    rowNow = -1;
    painter.invalidate();
  };

  const show = (clip, frame, mirror) => {
    adoptAtlasIfReady();
    painter.draw(clip.name, frame, !!mirror);
    if (clip.name !== clipNow) {
      clipNow = clip.name;
      sprite.setAttribute('data-clip', clip.name);
    }
    const row = atlas ? rowOfClip(atlas, clip.name) : 0;
    if (row !== rowNow) {
      rowNow = row;
      strip.style.backgroundPosition = `0 ${-row * cell}px`;
    }
    if (frame !== frameNow) {
      frameNow = frame;
      strip.style.transform = `translate3d(${-frame * cell}px,0,0)`;
    }
    if (mirror !== mirrorNow) {
      mirrorNow = mirror;
      sprite.setAttribute('data-mirror', String(!!mirror));
    }
  };

  const place = (x, y) => {
    feet = { x, y };
    const c = cellAt(geo, x, y);
    const key = `${c.left},${c.top},${snapX},${snapY}`;
    if (key !== lastPos) {
      lastPos = key;
      wrap.style.transform = `translate3d(${c.left + snapX}px,${c.top + snapY}px,0)`;
    }
  };

  const container = wrap.offsetParent || wrap.parentElement;
  const unsnap = addSnapTarget({
    origin() {
      if (!container || !container.isConnected) return null;
      const r = container.getBoundingClientRect();
      return { x: r.left + container.clientLeft, y: r.top + container.clientTop };
    },
    apply(x, y) {
      if (x === snapX && y === snapY) return;
      snapX = x;
      snapY = y;
      if (feet) place(feet.x, feet.y);
    },
  });

  const shade = (sh) => {
    const sq = sh.sq || 1;
    const key = `${sh.surface}|${Math.round(sh.x)}|${Math.round(sh.y ?? 0)}|${sh.o.toFixed(2)}|${sh.sx.toFixed(2)}|${sq.toFixed(2)}`;
    if (key === lastShadow) return;
    lastShadow = key;
    ['HOME', 'LEDGE'].forEach((surf) => {
      const el = shadows[surf];
      if (!el) return;
      el.style.opacity = sh.surface === surf ? String(sh.o) : '0';
      el.style.transform = `translate3d(${Math.round(sh.x)}px,${Math.round(sh.y ?? geo[surf].y)}px,0) scale(${(sh.sx * sq).toFixed(3)},${(2 - sq).toFixed(3)})`;
    });
  };

  return {
    resync() { frameNow = -1; rowNow = -1; clipNow = ''; painter.invalidate(); },
    dispose() { unsnap(); },
    /* T = absolute clock ms, t = loop time, run = loop count. */
    update(T, t, run) {
      const track = run === 0 ? tracks.first : tracks.loop;
      if (track !== lastTrack) {
        lastTrack = track;
        idx = segIndexAt(track, t);
      }
      const { segs } = track;
      if (t < segs[idx].start || t >= segs[idx].end) {
        if (idx + 1 < segs.length && t >= segs[idx + 1].start && t < segs[idx + 1].end) idx += 1;
        else idx = segIndexAt(track, t);
      }
      const seg = segs[idx];
      const clip = clipOf(track, seg);
      const shown = clip.delivered ? clip : resolveClip(clip.standIn);
      show(shown, clip.delivered ? frameAt(clip, localAt(track, seg, t, T), seg.pose) : 0, seg.mirror);
      const p = scene && scene.homeAt ? scene.homeAt(t) : posAt(track, seg, t, pts);
      place(p.x, p.y);
      const sh = { ...shadowAt(track, seg, t, pts), x: p.x, y: p.y };
      if (typeof seg.pos !== 'string' && clip.airborne) {
        // the surface gives a little for 160 ms after a landing
        const since = t - (seg.start + clip.airborne[1] - clip.trimIn);
        if (since >= 0 && since < 160) sh.sq = 1 + 0.2 * Math.sin((since / 160) * Math.PI);
      }
      shade(sh);
    },
    /* A still pose (posters): clip + clip-local ms at a surface. */
    pose({ clip, local, pos }) {
      const c = CLIPS[clip] || CLIPS.rest;
      show(c, frameAt(c, local), false);
      const at = geo[pos] || geo.HOME;
      place(at.x, at.y);
      shade({ surface: pos, x: at.x, o: 1, sx: 1 });
    },
  };
}
